import {
  Button,
  ButtonProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToken,
  VStack,
  HStack,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { ExpertiseBodyDamageType, ExpertiseBodyDamageTypeLabel, ExpertiseCarBodyPartName } from 'types';
import { ExpertiseBodyworkExpertiseStates } from '../models';

export interface ExpertiseBodyworkExpertiseStateSelectorProps extends Omit<ButtonProps, 'children'> {
  expertiseKeyName: ExpertiseCarBodyPartName;
  displayId: number;
  onBodyworkExpertiseChanged: (
    expertiseKeyName: ExpertiseCarBodyPartName,
    expertiseBodyworkExpertiseState: ExpertiseBodyDamageType, // The clicked new state
  ) => void;
  value: ExpertiseBodyDamageType[]; // Multi-select values
  label: string;
  isDisabled?: boolean;
}
export const ExpertiseBodyworkExpertiseStateSelector: React.FC<ExpertiseBodyworkExpertiseStateSelectorProps> = ({
  value,
  onBodyworkExpertiseChanged,
  expertiseKeyName,
  displayId,
  label,
  isDisabled,
  ...buttonProps
}) => {
  const { left, right } = buttonProps;
  const [
    originalColor,
    paintedColor,
    changedColor,
    halfPaintedColor,
    localPaintedColor,
    removedColor,
    cutColor,
    processedColor,
    foilCoatedColor,
    damagedColor,
    sunroofColor,
    plasticColor,
  ] = useToken(
    // the key within the theme, in this case `theme.colors`
    'colors',
    // the subkey(s), resolving to `theme.colors.red.100`
    [
      'gray.900',
      'red.400',
      'green.300',
      'orange.400',
      'orange.900',
      'cyan.400',
      'purple.400',
      'pink.400',
      'yellow.400',
      'red.600',
      'yellow.600',
      'teal.400',
    ],
    // a single fallback or fallback array matching the length of the previous arg
  );

  const lookupTable = useMemo(
    () => ({
      [ExpertiseBodyDamageType.Original]: {
        bg: originalColor,
        label: ExpertiseBodyworkExpertiseStates.Original,
      },
      [ExpertiseBodyDamageType.Painted]: {
        bg: paintedColor,
        label: ExpertiseBodyworkExpertiseStates.Painted,
      },
      [ExpertiseBodyDamageType.Changed]: {
        bg: changedColor,
        label: ExpertiseBodyworkExpertiseStates.Changed,
      },
      [ExpertiseBodyDamageType.HalfPainted]: {
        bg: halfPaintedColor,
        label: ExpertiseBodyworkExpertiseStates.HalfPainted,
      },
      [ExpertiseBodyDamageType.LocallyPainted]: {
        bg: localPaintedColor,
        label: ExpertiseBodyworkExpertiseStates.LocallyPainted,
      },
      [ExpertiseBodyDamageType.Removed]: {
        bg: removedColor,
        label: ExpertiseBodyworkExpertiseStates.Removed,
      },
      [ExpertiseBodyDamageType.Cutting]: {
        bg: cutColor,
        label: ExpertiseBodyworkExpertiseStates.Cutting,
      },
      [ExpertiseBodyDamageType.Processed]: {
        bg: processedColor,
        label: ExpertiseBodyworkExpertiseStates.Processed,
      },
      [ExpertiseBodyDamageType.FoilCoated]: {
        bg: foilCoatedColor,
        label: ExpertiseBodyworkExpertiseStates.FoilCoated,
      },
      [ExpertiseBodyDamageType.Damaged]: {
        bg: damagedColor,
        label: ExpertiseBodyworkExpertiseStates.Damaged,
      },
      [ExpertiseBodyDamageType.Sunroof]: {
        bg: sunroofColor,
        label: ExpertiseBodyworkExpertiseStates.Sunroof,
      },
      [ExpertiseBodyDamageType.Plastic]: {
        bg: plasticColor,
        label: ExpertiseBodyworkExpertiseStates.Plastic,
      },
    }),
    [],
  );

  const onStateChange = (expertiseBodyworkExpertiseStates: ExpertiseBodyDamageType) => () => {
    onBodyworkExpertiseChanged(expertiseKeyName, expertiseBodyworkExpertiseStates);
  };

  let buttonBg = 'gray.900';
  if (value.length === 1 && lookupTable[value[0]]) {
    buttonBg = lookupTable[value[0]].bg;
  } else if (value.length > 1) {
    buttonBg = 'purple.600';
  }

  let buttonText = '';
  if (value.length > 0) {
    if (value.length > 1) {
      buttonText = value.join(',');
    } else {
      [buttonText] = value;
    }
  }

  let buttonTransform: string | undefined;
  if (value.length > 1) {
    if (left !== undefined) {
      buttonTransform = 'translateX(calc(-50% + 1rem))';
    } else if (right !== undefined) {
      buttonTransform = 'translateX(calc(50% - 1rem))';
    }
  }

  return (
    <Menu closeOnSelect={false}>
      <MenuButton
        as={Button}
        width="auto"
        height="auto"
        minW={8}
        minH={8}
        p={0}
        borderRadius="full"
        variant="solid"
        {...buttonProps}
        transform={buttonTransform}
        bg={value.length > 1 ? 'transparent' : buttonBg}
        _hover={{
          opacity: 0.8,
        }}
        _active={{
          opacity: 0.8,
        }}
      >
        {value.length > 1 ? (
          <HStack spacing={-1} p={1}>
            {value.map((v) => (
              <VStack
                key={v}
                w={8}
                h={8}
                borderRadius="full"
                bg={lookupTable[v]?.bg || 'gray.900'}
                justify="center"
                align="center"
                border="2px solid white"
                boxShadow="sm"
              >
                <Text fontSize="xs" color="white" fontWeight="bold">
                  {v}
                </Text>
              </VStack>
            ))}
          </HStack>
        ) : (
          <VStack spacing={0} w={8} h={8} justify="center" borderRadius="full">
            <Text fontSize="sm" color="white">
              {buttonText}
            </Text>
          </VStack>
        )}
      </MenuButton>
      <MenuList maxH="300px" overflowY="auto">
        <MenuItem disabled isDisabled>
          {label}
        </MenuItem>
        {!isDisabled && (
          <>
            {[
              ExpertiseBodyDamageType.Original,
              ExpertiseBodyDamageType.Painted,
              ExpertiseBodyDamageType.Changed,
              ExpertiseBodyDamageType.HalfPainted,
              ExpertiseBodyDamageType.LocallyPainted,
              ExpertiseBodyDamageType.Removed,
              ExpertiseBodyDamageType.Cutting,
              ExpertiseBodyDamageType.Processed,
              ExpertiseBodyDamageType.FoilCoated,
              ExpertiseBodyDamageType.Plastic,
              ExpertiseBodyDamageType.Damaged,
            ].map((type) => (
              <MenuItem
                key={type}
                onClick={onStateChange(type)}
                bg={value.includes(type) ? 'gray.100' : undefined}
                color={value.includes(type) ? 'black' : undefined}
                _hover={{ bg: 'gray.200', color: 'black' }}
              >
                {ExpertiseBodyDamageTypeLabel.get(type)} {value.includes(type) && '✓'}
              </MenuItem>
            ))}

            {/* Show sunroof option only on tavan of car */}
            {displayId === ExpertiseCarBodyPartName.Tavan && (
              <MenuItem
                onClick={onStateChange(ExpertiseBodyDamageType.Sunroof)}
                bg={value.includes(ExpertiseBodyDamageType.Sunroof) ? 'gray.100' : undefined}
                color={value.includes(ExpertiseBodyDamageType.Sunroof) ? 'black' : undefined}
                _hover={{ bg: 'gray.200', color: 'black' }}
              >
                {ExpertiseBodyDamageTypeLabel.get(ExpertiseBodyDamageType.Sunroof)}{' '}
                {value.includes(ExpertiseBodyDamageType.Sunroof) && '✓'}
              </MenuItem>
            )}
          </>
        )}
      </MenuList>
    </Menu>
  );
};

ExpertiseBodyworkExpertiseStateSelector.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  position: 'absolute',
  // eslint-disable-next-line react/default-props-match-prop-types
  bg: 'gray.900',
};
